/**
 * Shader code and uniform values for design material layers, a stack of up to 4 textures that are overlaid on each other using configurable blending, masking and effects.
 * @module
 */
import { bitCheck, bitSetTo } from "common/utils/bitfield";
import * as THREE from "three";
import { HAVE_IMAGEBITMAP } from "../constants";
import { defaultMaskParams, MAX_SDF_PATTERNS, NUM_LAYERS } from "./designMaterialDefaults";
import { initUVMatrixFromNormalMap, mixSRGBColorStrings } from "./designMaterialParams";
import type { DesignMaterialLoadOptions, MaterialParamsWithoutSdf } from "./designMaterialRuntimeConfig";
import { ComponentViewMode } from "./designMaterialRuntimeConfig";
import type { DesignMaterialShaderChunks } from "./designMaterialShaderChunks";
import type { LayerParamsPreprocessed, MaskParams } from "./designMaterialTypes";
import type { LoadTextureFunc } from "./loadTextureFuncType";
import { blendModeCoefficients, blendModeInt, dynamicBlendingChunk } from "./shaders/blending";
import { OUTSIDE_RANDOM_MASK_VALUE } from "./shaders/utils";
import { atlasSlotFromTextureName, atlasTextureForRenderer } from "./TextureAtlas";
import { TEXTURE_ATLAS_TEXTURE_WIDTH } from "./textureAtlasConstants";
import { fArrayUniform, fUniform, iUniform, initUVMatrixFromParams, v3Uniform, v4Uniform } from "./uniformHelpers";

const flipYMatrix = new THREE.Matrix3().set(1, 0, 0, 0, -1, 0, 0, 0, 1);

export function includeDesignMaterialLayers(
  options: DesignMaterialLoadOptions,
  useWebgl2TextureAtlas: boolean,
  designMaterialShaderChunks: DesignMaterialShaderChunks,
  finalParams: MaterialParamsWithoutSdf,
) {
  if (!options.disableLayers) {
    const heightDeclArraySize = NUM_LAYERS;

    designMaterialShaderChunks.fragmentDefinitions.push(
      `vec4 dmColorRoughMetalHeightStrength0;
      vec4 dmColorRoughMetalHeightStrength1;
      vec4 dmColorRoughMetalHeightStrength2;
      vec4 dmColorRoughMetalHeightStrength3;

      uniform float dmLayerTextureGamma;`,
    );

    if (useWebgl2TextureAtlas) {
      designMaterialShaderChunks.fragmentDefinitions.push(
        `uniform highp sampler2DArray dmTextureStore;

        float getTextureMask(int texIndex, float contrast, vec2 uv) {
          return smoothstep(contrast, 1.0 - contrast, pow(texture(dmTextureStore, vec3(uv, texIndex)).r, dmLayerTextureGamma));
        }

        mat3 dmHeightMat[${heightDeclArraySize}];
        int dmHeightTexture[${heightDeclArraySize}];
        float dmHeightStrength[${heightDeclArraySize}];
        int dmCurrentHeightIndex = 0;

        uniform float dmHeightLodBias[${heightDeclArraySize}];`,
      );
    } else {
      designMaterialShaderChunks.fragmentDefinitions.push(
        `float getTextureMask(sampler2D tex, float contrast, vec2 uv) {
          return smoothstep(contrast, 1.0 - contrast, pow(texture2D(tex, uv).r, dmLayerTextureGamma));
        }`,
      );
    }

    if (useWebgl2TextureAtlas) {
      // Height Generation
      if (options.reproduceImplicitLodIntelBug) {
        // This is intended to reproduce a bug on Intel GPU drivers (version 31.0.101.5330), so that we can provide them a test case.
        designMaterialShaderChunks.heightWrites.push(
          `for (int dmHI = 0; dmHI < dmCurrentHeightIndex; ++dmHI) {
            vec2 heightUv = (dmHeightMat[dmHI] * vec3( uv, 1 ) ).xy;
            height -= pow(texture(dmTextureStore, vec3( heightUv, dmHeightTexture[dmHI] )).r, dmLayerTextureGamma) * dmHeightStrength[dmHI];
          }`,
        );
      } else {
        // Normally we use manually computed LOD.
        designMaterialShaderChunks.heightWrites.push(
          `for (int dmHI = 0; dmHI < dmCurrentHeightIndex; ++dmHI) {
            vec2 heightUv = (dmHeightMat[dmHI] * vec3( uv, 1 ) ).xy;
            height -= pow(textureLod(dmTextureStore, vec3( heightUv, dmHeightTexture[dmHI] ), lodFromUV + dmHeightLodBias[dmHI]).r, dmLayerTextureGamma) * dmHeightStrength[dmHI];
          }`,
        );
      }
    }
    for (let i = 0; i < NUM_LAYERS; i++) {
      includeLayer(
        i < finalParams.layers.length ? finalParams.layers[i].useForHeight : false,
        i,
        useWebgl2TextureAtlas,
        designMaterialShaderChunks,
      );
    }
  }
}

function includeLayer(
  useForHeight: boolean,
  i: number,
  useWebgl2TextureAtlas: boolean,
  designMaterialShaderChunks: DesignMaterialShaderChunks,
) {
  // Frag definitions
  // Note that in some cases we pack different parameters to the same uniform as an optimization.
  // dmLayerColorGradient is packed so that each column contains one color. The last value in the column is the position of the color stop.
  const layerFragDefinitions = `
    #ifdef USE_WEBGL2_TEXTURE_ATLAS
    uniform int dmLayerTexture${i};
    #else
    uniform sampler2D dmLayerTexture${i};
    #endif
    uniform int dmBlendMode${i};
    uniform vec3 dmBlendCoefficients${i};
    uniform mat4 dmLayerColorGradient${i};
    uniform vec4 dmColorRoughMetalHeight${i};

    uniform vec4 dmLayerChoice${i};
    uniform vec4 dmMaskCombiner${i};
    uniform vec4 dmMaskInvert_DepthOverlay_Threshold_EdgeWearOverlay${i};

    // Texture mask
    #ifdef USE_WEBGL2_TEXTURE_ATLAS
    uniform int dmMaskTexture${i};
    #else
    uniform sampler2D dmMaskTexture${i};
    #endif
    uniform float dmMaskContrast${i};

    uniform int dmSdfPatternMask${i};

    uniform float dmRandomUvOffsetStrength${i};

    uniform float dmRandomMaskUniqueness${i};

    vec2 dmRandomUvOffset${i};

    uniform mat3 dmUvMat${i};
    uniform mat3 dmMaskUvMat${i};
    `;

  // Height Generation
  if (useForHeight && !useWebgl2TextureAtlas) {
    designMaterialShaderChunks.heightWrites.push(
      `height -= pow(texture2D(dmLayerTexture${i}, ( dmUvMat${i} * vec3( uv, 1 ) ).xy + dmRandomUvOffset${i}).r, dmLayerTextureGamma) * dmColorRoughMetalHeightStrength${i}.w;`,
    );
  }

  designMaterialShaderChunks.fragmentDefinitions.push(layerFragDefinitions);

  // The magic numbers are picked so that they result in a good distribution of samples in case the input values are texture samples quantized to n/255.
  // These ones are used for the random UV offset.
  const pseudoRandomMultipliersX = [144.1, 175.4, 203.1, 315.3];
  const pseudoRandomMultipliersY = [167.8, 194.1, 227.3, 235.9];
  if (useWebgl2TextureAtlas && !HAVE_IMAGEBITMAP) {
    // The WebGL2 texture atlas is flipped in the Y direction, so to match behavior with WebGL 1.0, the random texture offset is negated.
    for (let i = 0; i < pseudoRandomMultipliersY.length; ++i) {
      pseudoRandomMultipliersY[i] = -pseudoRandomMultipliersY[i];
    }
  }
  // These ones are used to modify the SDF random mask per layer.
  const pseudoRandomMultipliersMask = ["231.0", "331.0", "547.0", "811.0"];

  // Evaluate layer strength based on mask etc. This needs to be done before height is queried.
  designMaterialShaderChunks.fragmentEvaluateLayers.push(
    `bool dmLayerEnabled${i} = any(notEqual(dmColorRoughMetalHeight${i}, vec4(0.0)));
    float dmLayerSample${i};
    float dmMaskSample${i};
    vec3 dmColor${i};
    if (dmLayerEnabled${i}) {
      vec2 dmUv${i} = ( dmUvMat${i} * vec3( uvWithParallax, 1 ) ).xy;
      vec2 baseRandomUvOffset = fract(vec2(mainRandomMask * ${pseudoRandomMultipliersX[i]}, mainRandomMask * (${
        pseudoRandomMultipliersY[i]
      }))) - 0.5;
      float layerRandomMask = mainRandomMask;
      if (mainRandomMask >= 0.5) {
        layerRandomMask = mix(mainRandomMask, fract(mainRandomMask * ${
          pseudoRandomMultipliersMask[i]
        }) * 0.5 + 0.5, dmRandomMaskUniqueness${i});
      }
      dmRandomUvOffset${i} = dmRandomUvOffsetStrength${i} * baseRandomUvOffset;
      float layerTextureSample = 1.0;
      if (dmLayerChoice${i}.w != 0.0) {
        vec2 layerUv = dmUv${i} + dmRandomUvOffset${i};
        #ifdef USE_WEBGL2_TEXTURE_ATLAS
        layerTextureSample = pow(texture(dmTextureStore, vec3(layerUv, dmLayerTexture${i})).r, dmLayerTextureGamma);
        #else
        layerTextureSample = pow(texture2D(dmLayerTexture${i}, layerUv).r, dmLayerTextureGamma);
        #endif
      }
      dmLayerSample${i} = clamp(dot(dmLayerChoice${i}, vec4(layerRandomMask, depthForMask, 1.0, layerTextureSample)), 0.0, 1.0);

      vec4 colorWeights = vec4(0);
      if (dmLayerSample${i} <= dmLayerColorGradient${i}[1].a) { // Gradient start
        colorWeights[1] = max((dmLayerSample${i} - dmLayerColorGradient${i}[0].a) / (dmLayerColorGradient${i}[1].a - dmLayerColorGradient${i}[0].a), 0.0);
        colorWeights[0] = 1.0 - colorWeights[1];
      } else if (dmLayerSample${i} >= dmLayerColorGradient${i}[2].a) { // Gradient end
        colorWeights[3] = min((dmLayerSample${i} - dmLayerColorGradient${i}[2].a) / (dmLayerColorGradient${i}[3].a - dmLayerColorGradient${i}[2].a), 1.0);
        colorWeights[2] = 1.0 - colorWeights[3];
      } else { // Gradient middle
        colorWeights[2] = (dmLayerSample${i} - dmLayerColorGradient${i}[1].a) / (dmLayerColorGradient${i}[2].a - dmLayerColorGradient${i}[1].a);
        colorWeights[1] = 1.0 - colorWeights[2];
      }
      dmColor${i} = (dmLayerColorGradient${i} * colorWeights).rgb;

      float textureMask = 1.0;
      if ((sdfPatternIDMask & dmSdfPatternMask${i}) == 0) {
        layerRandomMask = ${OUTSIDE_RANDOM_MASK_VALUE.toFixed(1)};
      }
      if (dmMaskCombiner${i}.w != 0.0) {
        vec2 dmMaskUv${i} = ( dmMaskUvMat${i} * vec3( uvWithParallax, 1 ) ).xy;
        vec2 maskOffset = dmRandomUvOffsetStrength${i} * baseRandomUvOffset;
        textureMask = getTextureMask(dmMaskTexture${i}, dmMaskContrast${i}, dmMaskUv${i} + maskOffset);
      }
      float dmLayerMask${i} = dot(dmMaskCombiner${i}, vec4(layerRandomMask, depthForMask, 1.0, textureMask));

      ${
        "" /* The clamping lower limit -0.001 is differs from 0 so little that it shouldn't visibly affect the mask,
              but it prevents excluded regions like the back plane or masked out patterns reappearing if the mask threshold value is 0. */
      }
      dmLayerMask${i} = clamp(dmLayerMask${i}, -0.001, 1.0);
      dmLayerMask${i} *= mix(1.0, min(depthForMask * sdfDepthForMaskMultiplier, 1.0), dmMaskInvert_DepthOverlay_Threshold_EdgeWearOverlay${i}.y);
      dmLayerMask${i} *= mix(1.0, edgeWearMask, dmMaskInvert_DepthOverlay_Threshold_EdgeWearOverlay${i}.w);
      if (dmMaskInvert_DepthOverlay_Threshold_EdgeWearOverlay${i}.z >= 0.0) {
        dmLayerMask${i} = step(dmMaskInvert_DepthOverlay_Threshold_EdgeWearOverlay${i}.z, dmLayerMask${i});
      }
      dmMaskSample${i} = 0.5 - dmMaskInvert_DepthOverlay_Threshold_EdgeWearOverlay${i}.x * 0.5 + dmMaskInvert_DepthOverlay_Threshold_EdgeWearOverlay${i}.x * dmLayerMask${i};
      dmColorRoughMetalHeightStrength${i} = dmColorRoughMetalHeight${i} * dmMaskSample${i};
      #ifdef USE_WEBGL2_TEXTURE_ATLAS
      if (abs(dmColorRoughMetalHeight${i}.w) > 0.0) {
        dmHeightMat[dmCurrentHeightIndex] = dmUvMat${i};
        dmHeightMat[dmCurrentHeightIndex][2][0] += dmRandomUvOffset${i}.x;
        dmHeightMat[dmCurrentHeightIndex][2][1] += dmRandomUvOffset${i}.y;
        dmHeightTexture[dmCurrentHeightIndex] = dmLayerTexture${i};
        dmHeightStrength[dmCurrentHeightIndex] = dmColorRoughMetalHeightStrength${i}.w;
        ++dmCurrentHeightIndex;
      }
      #endif
    }`,
  );

  // Frag Pre-light
  designMaterialShaderChunks.fragmentPrelight.push(
    `if (dmLayerEnabled${i}) {
      ${dynamicBlendingChunk(
        "dmBlendMode" + i,
        "dmBlendCoefficients" + i,
        "diffuseColor",
        "dmColor" + i,
        "dmColorRoughMetalHeightStrength" + i + ".x",
      )}
      roughnessFactor += dmLayerSample${i}*dmColorRoughMetalHeightStrength${i}.y;
      metalnessFactor += dmLayerSample${i}*dmColorRoughMetalHeightStrength${i}.z;
    }`,
  );
}

function layerMaskTextureName(
  finalParams: MaterialParamsWithoutSdf,
  layerParams: LayerParamsPreprocessed,
): string | undefined {
  const layerHasTextureMask = layerParams.maskID.startsWith("Mask");
  if (layerHasTextureMask) {
    const maskLayerIndex = parseInt(layerParams.maskID.substring(4));
    const maskParams = finalParams.masks[maskLayerIndex];
    return maskParams.textureName;
  }
  return undefined;
}

export async function loadLayerTextures(
  finalParams: MaterialParamsWithoutSdf,
  useWebgl2TextureAtlas: boolean,
  loadAndTrackTextureInAtlas: (name: string) => Promise<void>,
  loadAndTrackTexture: (name: string, overrideFormat: THREE.PixelFormat) => void,
  loadTexture: LoadTextureFunc,
  layerTextureFormat,
  textureCache,
) {
  for (let i = 0; i < finalParams.layers.length; i++) {
    const layerParams = finalParams.layers[i];

    if (layerParams.srcID === "texture") {
      const textureName = layerParams.textureName;
      if (layerParams.useForHeight && Math.abs(layerParams.heightInfluence) > 0) {
        if (useWebgl2TextureAtlas) {
          const texPromise = loadAndTrackTextureInAtlas(textureName);
          // We should wait on the texture load to finish since we compute a filter size based on the texture dimensions.
          await texPromise;
        } else {
          const [tex, texPromise] = loadTexture(textureName, layerTextureFormat);
          await texPromise;
          textureCache[textureName] = tex;
        }
      } else {
        if (useWebgl2TextureAtlas) {
          void loadAndTrackTextureInAtlas(textureName);
        } else {
          loadAndTrackTexture(textureName, layerTextureFormat);
        }
      }
    }
    const maskTexName = layerMaskTextureName(finalParams, layerParams);
    if (maskTexName) {
      if (useWebgl2TextureAtlas) {
        void loadAndTrackTextureInAtlas(maskTexName);
      } else {
        loadAndTrackTexture(maskTexName, layerTextureFormat);
      }
    }
  }
}

function loadLayerMaskFromAtlas(
  finalParams: MaterialParamsWithoutSdf,
  layerParams: LayerParamsPreprocessed,
): { maskParams: MaskParams; maskTexture?: number } {
  const layerHasTextureMask = layerParams.maskID.startsWith("Mask");
  if (layerHasTextureMask) {
    const maskLayerIndex = parseInt(layerParams.maskID.substring(4));
    const maskParams = finalParams.masks[maskLayerIndex];
    return { maskParams, maskTexture: atlasSlotFromTextureName(maskParams.textureName) };
  }
  return { maskParams: defaultMaskParams, maskTexture: undefined };
}

function loadLayerMaskFromTextureCache(
  finalParams: MaterialParamsWithoutSdf,
  layerParams: LayerParamsPreprocessed,
  textureCache: { [name: string]: THREE.Texture },
): { maskParams: MaskParams; maskTexture?: THREE.Texture } {
  const layerHasTextureMask = layerParams.maskID.startsWith("Mask");
  if (layerHasTextureMask) {
    const maskLayerIndex = parseInt(layerParams.maskID.substring(4));
    const maskParams = finalParams.masks[maskLayerIndex];
    return { maskParams, maskTexture: textureCache[maskParams.textureName] };
  }
  return { maskParams: defaultMaskParams, maskTexture: undefined };
}

export function setLayerUniforms(
  options: DesignMaterialLoadOptions,
  useWebgl2TextureAtlas: boolean,
  uniforms: { [name: string]: any },
  renderer: THREE.WebGLRenderer,
  finalParams: MaterialParamsWithoutSdf,
  textureCache: { [name: string]: THREE.Texture },
  depthForMaskMultiplier: number,
) {
  let minWidthOfHeightMapPixel: number | undefined = undefined; // In uv space.
  const heightLodBias = [0, 0, 0, 0];
  let heightLayerCount = 0;

  if (!options.disableLayers) {
    // We upload SRGB images to textures that are interpreted as linear, so we need to gamma-correct them in the shader.
    // Native SRGB encoding is not supported for RED textures, which we use to save memory.
    uniforms.dmLayerTextureGamma = { value: finalParams.useLegacyGamma ? 1.0 : 2.2 };

    if (useWebgl2TextureAtlas) {
      uniforms.dmTextureStore = { value: atlasTextureForRenderer(renderer, options.reproduceImplicitLodIntelBug) };
    }

    function setupDisabledLayer(i: number) {
      uniforms["dmLayerChoice" + i] = v4Uniform(0, 0, 0, 0);
      uniforms["dmColorRoughMetalHeight" + i] = v4Uniform(0, 0, 0, 0);
      if (useWebgl2TextureAtlas) {
        uniforms["dmLayerTexture" + i] = { value: 0 };
      } else {
        uniforms["dmLayerTexture" + i] = { type: "t", value: null };
      }
    }

    for (let i = 0; i < finalParams.layers.length; i++) {
      const layerParams = finalParams.layers[i];
      if (layerParams.srcID === "texture") {
        if (useWebgl2TextureAtlas) {
          uniforms["dmLayerTexture" + i] = { value: atlasSlotFromTextureName(layerParams.textureName) };
        } else {
          uniforms["dmLayerTexture" + i] = {
            type: "t",
            value: textureCache[layerParams.textureName],
          };
        }
      }

      const sdfRandomMaskSrc = layerParams.srcID === "SDFRandom";
      const sdfDepthMaskSrc = layerParams.srcID === "SDFDepthMask";
      uniforms["dmLayerChoice" + i] = v4Uniform(
        sdfRandomMaskSrc && !options.disableSDF ? 1 : 0,
        sdfDepthMaskSrc && !options.disableSDF ? depthForMaskMultiplier : 0,
        layerParams.srcID === "none" || (options.disableSDF && (sdfDepthMaskSrc || sdfRandomMaskSrc)) ? 1 : 0,
        layerParams.srcID === "texture" ? 1 : 0,
      );

      const uvMat = new THREE.Matrix3();
      if (layerParams.uvTransformID === "normalMap") {
        initUVMatrixFromNormalMap(finalParams, uvMat);
      } else {
        initUVMatrixFromParams(layerParams, uvMat);
      }
      if (useWebgl2TextureAtlas && !HAVE_IMAGEBITMAP) {
        // Flip Y of the UV transform since y can't be flipped when uploading 3D textures.
        uvMat.premultiply(flipYMatrix);
      }
      uniforms["dmUvMat" + i] = { type: "m3", value: uvMat };

      uniforms["dmBlendMode" + i] = { type: "i", value: blendModeInt(layerParams.blendMode) };
      const blendCoefficients = blendModeCoefficients(layerParams.blendMode);
      uniforms["dmBlendCoefficients" + i] = v3Uniform(blendCoefficients[0], blendCoefficients[1], blendCoefficients[2]);

      // The shader always uses a fixed amount of 4 colors.
      // In case our gradient is specified with less colors, we interpolate the missing colors here and then pass them to the shader.
      const color0 = layerParams.useBaseColorAsColor0 ? finalParams.color : layerParams.color0;
      const color1 = layerParams.useBaseColorAsColor1 ? finalParams.color : layerParams.color1;
      const color2 = layerParams.useBaseColorAsColor2 ? finalParams.color : layerParams.color2;
      const color3 = layerParams.useBaseColorAsColor3 ? finalParams.color : layerParams.color3;
      let gradientColors;
      let gradientColorPositions;
      if (layerParams.colorCount === 2) {
        gradientColors = [
          color0,
          mixSRGBColorStrings(color0, color1, 1 / 3),
          mixSRGBColorStrings(color0, color1, 2 / 3),
          color1,
        ];
        // Clamp the color positions to avoid divide by zero.
        const color0Pos = Math.min(0.999, layerParams.color0Pos);
        const color1Pos = Math.max(color0Pos + 0.001, layerParams.color1Pos);
        gradientColorPositions = [
          color0Pos,
          color0Pos * (2 / 3) + color1Pos * (1 / 3),
          color0Pos * (1 / 3) + color1Pos * (2 / 3),
          color1Pos,
        ];
      } else if (layerParams.colorCount === 3) {
        gradientColors = [color0, mixSRGBColorStrings(color0, color1, 1 / 2), color1, color2];
        // Clamp the color positions to avoid divide by zero.
        const color0Pos = Math.min(0.998, layerParams.color0Pos);
        const color1Pos = Math.max(color0Pos + 0.001, Math.min(0.999, layerParams.color1Pos));
        const color2Pos = Math.max(color1Pos + 0.001, layerParams.color2Pos);
        gradientColorPositions = [color0Pos, (color0Pos + color1Pos) / 2, color1Pos, color2Pos];
      } else if (layerParams.colorCount === 4) {
        gradientColors = [color0, color1, color2, color3];
        // Clamp the color positions to avoid divide by zero.
        const color0Pos = Math.min(0.997, layerParams.color0Pos);
        const color1Pos = Math.max(color0Pos + 0.001, Math.min(0.998, layerParams.color1Pos));
        const color2Pos = Math.max(color1Pos + 0.001, Math.min(0.999, layerParams.color2Pos));
        const color3Pos = Math.max(color2Pos + 0.001, layerParams.color3Pos);
        gradientColorPositions = [color0Pos, color1Pos, color2Pos, color3Pos];
      }

      if (layerParams.invertInput) {
        gradientColors = gradientColors.reverse();
        gradientColorPositions = gradientColorPositions.reverse();
        for (let i = 0; i < gradientColorPositions.length; ++i) {
          gradientColorPositions[i] = 1.0 - gradientColorPositions[i];
        }
      }

      for (let i = 0; i < gradientColors.length; ++i) {
        gradientColors[i] = new THREE.Color(gradientColors[i]);
      }

      const colorMat = new THREE.Matrix4();
      colorMat.set(
        gradientColors[0].r,
        gradientColors[1].r,
        gradientColors[2].r,
        gradientColors[3].r,
        gradientColors[0].g,
        gradientColors[1].g,
        gradientColors[2].g,
        gradientColors[3].g,
        gradientColors[0].b,
        gradientColors[1].b,
        gradientColors[2].b,
        gradientColors[3].b,
        gradientColorPositions[0],
        gradientColorPositions[1],
        gradientColorPositions[2],
        gradientColorPositions[3],
      );

      uniforms["dmLayerColorGradient" + i] = { type: "m4", value: colorMat };
      uniforms["dmRandomUvOffsetStrength" + i] = fUniform(layerParams.randomUvOffsetStrength * 2);
      uniforms["dmRandomMaskUniqueness" + i] = fUniform(layerParams.randomMaskUniqueness);

      let uniformColorInfluence = layerParams.colorInfluence;
      if (layerParams.blendMode === "darken") {
        uniformColorInfluence = -uniformColorInfluence;
      }

      const strength = layerParams.strength;
      const layerHeightInfluence = layerParams.useForHeight ? layerParams.heightInfluence * strength : 0.0;
      uniforms["dmColorRoughMetalHeight" + i] = v4Uniform(
        uniformColorInfluence * strength,
        layerParams.roughnessInfluence * strength,
        layerParams.metalnessInfluence * strength,
        layerHeightInfluence,
      );

      // Height Generation
      if (layerHeightInfluence !== 0.0) {
        const textureSize = TEXTURE_ATLAS_TEXTURE_WIDTH; // All of our textures are a standard size.
        const layerMinWidthOfHeightMapPixel = Math.min(layerParams.scaleU, layerParams.scaleV) / textureSize;
        // The shader computes LOD based on the UV derivatives, compute a bias value that also takes the texture resolution and per-layer scale factor.
        // The -1.0 additional bias is used to compensate for the shader approximating the overall 2D bias as a sum of the x and y biases.
        heightLodBias[heightLayerCount] =
          Math.log2(Math.sqrt((1 / layerParams.scaleU) ** 2 + (1 / layerParams.scaleV) ** 2)) +
          Math.log2(TEXTURE_ATLAS_TEXTURE_WIDTH) -
          1.0;
        if (minWidthOfHeightMapPixel === undefined) {
          minWidthOfHeightMapPixel = layerMinWidthOfHeightMapPixel;
        } else {
          minWidthOfHeightMapPixel = Math.min(minWidthOfHeightMapPixel, layerMinWidthOfHeightMapPixel);
        }
        ++heightLayerCount;
      }

      let maskParams, layerHasTextureMask;
      if (useWebgl2TextureAtlas) {
        const { maskParams: atlasMaskParams, maskTexture } = loadLayerMaskFromAtlas(finalParams, layerParams);
        maskParams = atlasMaskParams;
        layerHasTextureMask = maskTexture !== undefined;
        uniforms["dmMaskTexture" + i] = { value: maskTexture !== undefined ? maskTexture : 0 };
      } else {
        const { maskParams: flatMaskParams, maskTexture } = loadLayerMaskFromTextureCache(
          finalParams,
          layerParams,
          textureCache,
        );
        maskParams = flatMaskParams;
        layerHasTextureMask = maskTexture !== undefined;
        uniforms["dmMaskTexture" + i] = { type: "t", value: maskTexture };
      }
      const maskUvMat = new THREE.Matrix3();

      if (layerParams.maskUvTransformID === "normalMap") {
        initUVMatrixFromNormalMap(finalParams, maskUvMat);
      } else {
        initUVMatrixFromParams(maskParams, maskUvMat);
      }
      if (useWebgl2TextureAtlas && !HAVE_IMAGEBITMAP) {
        // Flip Y of the UV transform since y can't be flipped when uploading 3D textures.
        maskUvMat.premultiply(flipYMatrix);
      }

      let patternMask = layerParams.sdfMaskPatterns;
      // WebGL 1.0 only supported 8 distinct mask values, since it doesn't have bitwise ops and they were packed to two vec4s.
      // We make the behavior consistent also on WebGL 2.0 by setting bits for patterns 9 to 12 to match pattern 8.
      // TODO: Remove this limitation
      const lastWebGL1PatternBit = bitCheck(patternMask, 7);
      for (let j = 8; j < MAX_SDF_PATTERNS; ++j) {
        patternMask = bitSetTo(patternMask, j, lastWebGL1PatternBit);
      }
      uniforms["dmSdfPatternMask" + i] = iUniform(patternMask);

      uniforms["dmMaskContrast" + i] = fUniform(maskParams.contrast * 0.5);
      uniforms["dmMaskUvMat" + i] = { type: "m3", value: maskUvMat };

      const sdfMasks = layerParams.maskID === "SDFMask";
      const hasMask = layerParams.maskID !== "none" && !(options.disableSDF && sdfMasks);
      const maskSdfDepthOverlay = sdfMasks && hasMask ? layerParams.sdfMaskDepthOverlay : 0.0;
      const invertMask =
        layerParams.invertMask && (layerParams.maskID !== "none" || layerParams.maskEdgeWearOverlay > 0.0);
      if (!hasMask) {
        uniforms["dmMaskCombiner" + i] = v4Uniform(0, 0, 1, 0); // Mask value is constant 1.
      } else if (layerParams.maskID === "SDFMask") {
        // The SDF random mask value in the shader will be OUTSIDE_RANDOM_MASK_VALUE (-500) if no tile is hit.
        // If a tile is hit it will be between 0.5 and 1.0.
        // If randomness is 0, then we want SDF random mask values >= 0.5 to result in layer mask matching "sdfMaskRandomAverage", and the negative values to result in layer mask 0.
        // If randomness is 0.5, then we want the layer mask values to be spread evenly between sdfMaskRandomAverage - 0.5 and sdfMaskRandomAverage + 0.5.
        // maskFudgeConstant is added so that black parts of the mask stay black with low values of sdfMaskRandomness.
        // It needs to be at least greater than the inverse of the value used for mainRandomMask when the ray doesn't hit any SDF tile.
        const maskFudgeConstant = 0.01;

        // The original SDF random mask range spreads 0.25 to each direction from the average, but we want to make it spread as far as the sdfMaskRandomness parameter.
        let maskRandomness = layerParams.sdfMaskRandomness * 4.0;
        let maskConstant = layerParams.sdfMaskRandomAverage - 0.75 * maskRandomness;

        if (layerParams.enableMaskThreshold) {
          maskRandomness = 2.0;
          maskConstant = -1.0;
        }

        uniforms["dmMaskCombiner" + i] = v4Uniform(maskRandomness + maskFudgeConstant, 0, maskConstant, 0);
      } else {
        const textureMaskMult = layerParams.enableMaskThreshold ? 1.0 : maskParams.strength;
        uniforms["dmMaskCombiner" + i] = v4Uniform(0, 0, 0, layerHasTextureMask ? textureMaskMult : 0);
      }

      uniforms["dmMaskInvert_DepthOverlay_Threshold_EdgeWearOverlay" + i] = v4Uniform(
        invertMask ? -1 : 1,
        maskSdfDepthOverlay,
        layerParams.enableMaskThreshold ? layerParams.maskThreshold : -1,
        layerParams.maskEdgeWearOverlay,
      );
    }

    for (let i = finalParams.layers.length; i < NUM_LAYERS; ++i) {
      // Fill in the rest of the shader parameters - the shader supports a fixed number of layers.
      setupDisabledLayer(i);
    }
  }

  if (minWidthOfHeightMapPixel === undefined) {
    minWidthOfHeightMapPixel = 0.1;
  }
  uniforms["dmHeightFilterWidth"] = fUniform(
    heightLayerCount > 0 ? minWidthOfHeightMapPixel * finalParams.layerHeightFilterWidth : -1.0,
  );
  if (useWebgl2TextureAtlas) {
    uniforms["dmHeightLodBias"] = fArrayUniform(heightLodBias);
  }
  if (options.componentViewMode !== ComponentViewMode.normalMapOnly) {
    uniforms["layerHeightAoStrength"] = fUniform(finalParams.layerHeightAoStrength);
  }
}
